//
// Copyright 2023 DXOS.org
//
import { BaseError, messageOf } from '@dxos/errors';
import { invariant } from '@dxos/invariant';
import { SystemError } from './base-errors.js';
export const reconstructError = (error) => {
    const { name, message, context } = error;
    return errorFromCode(name, message, context);
};
const errorRegistry = new Map();
export const registerError = (code, make) => {
    invariant(!errorRegistry.has(code), `Error code already registered: ${code}`);
    errorRegistry.set(code, make);
};
export const registerErrorNoArgs = (code, Constructor) => {
    registerError(code, () => new Constructor());
};
export const registerErrorMessageContext = (code, Constructor) => {
    registerError(code, (message, context) => new Constructor({ message, context }));
};
export const errorFromCode = (code, message, context) => {
    if (code && errorRegistry.has(code)) {
        return errorRegistry.get(code)(message, context);
    }
    else {
        return new BaseError(code ?? 'Error', { message, context });
    }
};
/** The `name` a thrown value carries, which is the field `encodeError` puts on the wire. */
const nameOf = (error) => {
    if (typeof error === 'object' && error !== null && 'name' in error) {
        const { name } = error;
        return typeof name === 'string' && name.length > 0 ? name : undefined;
    }
    return undefined;
};
/**
 * Narrows a thrown value for a service RPC's error channel.
 *
 * A DXOS error is returned as it is. Anything else is rebuilt as a `BaseError` under its own
 * `name` and stack, the fields `encodeError` puts on the wire and `decodeError` rebuilds from.
 * The wire format has no `cause` field, so `encodeError` folds the chain into the stack.
 */
export const toServiceError = (error) => {
    if (error instanceof BaseError) {
        return error;
    }
    const name = nameOf(error);
    const message = messageOf(error) ?? String(error);
    const wrapped = name === undefined ? new SystemError({ message, cause: error }) : new BaseError(name, { message, cause: error });
    const stack = typeof error === 'object' && error !== null ? error.stack : undefined;
    if (typeof stack === 'string') {
        // The origin's frames are what makes a host-side failure diagnosable once it reaches the client.
        wrapped.stack = stack;
    }
    return wrapped;
};
//# sourceMappingURL=helpers.js.map